import classnames from 'classnames';
import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Colors } from '../../../theme/src';
import { GenericButton, GenericButtonProps } from './GenericButton';

const useStyles = createUseStyles((theme: Theme) => ({
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: theme.iconSize.large,
    width: theme.iconSize.large,
    borderRadius: theme.borderRadius.std,
  },
  icon: {
    fontSize: theme.iconSize.small,
  },
  withBorder: {
    border: {
      width: 1,
      style: 'solid',
      color: 'transparent',
    },
  },
}));

const useMutedColorStyle = createUseStyles((theme: Theme) =>
  theme.colorList.reduce((acc, color) => {
    return {
      ...acc,
      [color]: {
        ...theme.mutedColorCombinations[color],
        ...theme.mutedColorsHover[color],
      },
    };
  }, {}),
);

const usePlainColorStyle = createUseStyles((theme: Theme) => ({
  ...theme.plainColorCombinaisons,
  white: {
    backgroundColor: theme.colors.white,
    color: theme.colors.inkPrimary,
    borderColor: theme.colors.inkPrimary,
  },
}));

interface SpecialProps {
  color: Colors | 'white';
  colorStyle?: 'plain' | 'muted';
  className?: string;
  icon: string;
  variant?: 'border';
}

export type ButtonIconProps = GenericButtonProps & SpecialProps;

export const ButtonIcon = ({
  color,
  colorStyle = 'plain',
  className,
  icon,
  variant,
  ...extra
}: ButtonIconProps) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const plainClasses: any = usePlainColorStyle({ theme });
  const mutedClasses: any = useMutedColorStyle({ theme });

  const colorClasses = colorStyle === 'muted' ? mutedClasses : plainClasses;

  return (
    <GenericButton
      type="button"
      {...extra}
      className={classnames(classes.button, colorClasses[color], className, {
        [classes.withBorder]: variant === 'border',
      })}
    >
      <i className={classnames(icon, classes.icon)} />
    </GenericButton>
  );
};
